/**
 * Settings > Agents.
 *
 * Personal: the "Subagent model", Cursor's Explore-subagent picker. The
 * Managed Deep Agents (research, drafting, PDF edits, organize) run on one
 * model for this attorney: **Default** (GPT-6 Luna), **Inherit from parent**
 * (the chat turn that starts them), or any catalog model; plus a Thinking
 * switch for models that reason. Saved on change to the lawyer profile
 * (`subagentModel` / `subagentThinking`) and applied to every agent run Flask
 * starts for this user.
 *
 * Firm-wide: "Approvals and events", whether every change asks first and
 * which client events wake the assistant (`FirmApprovalsSection`). Everyone
 * sees it; owners and admins edit it. Firm Instructions and the exhibit house
 * style live in Settings > Case Preparation; a line here points there.
 */
import { ArrowRight } from "lucide-react";
import { useMemo, useState, type FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

import { LawyerAPI } from "@/api/lawyerApi";
import { FirmApprovalsSection } from "@/components/settings/FirmApprovalsSection";
import { FIRM_INSTRUCTIONS_SECTION } from "@/components/settings/CasePreparationTab";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  DEFAULT_MODEL_ID,
  PROVIDER_LABEL,
  SUBAGENT_INHERIT_ID,
  type AgentModel,
  type ModelProvider
} from "@/features/agents/types";
import { useAgentSpecs } from "@/features/agents/useAgentSpecs";
import { setSubagentModelPick } from "@/redux/lawyer/lawyerSlice";
import { lawyerSelectors } from "@/redux/lawyer/selectors";

/** Radix Select rejects `""` as an item value; the stored Default is `""`. */
const DEFAULT_OPTION = "default";

export const DEFAULT_LABEL = "Default";
export const DEFAULT_MODEL_LABEL = "GPT-6 Luna";
export const INHERIT_LABEL = "Inherit from parent";

const AGENTS = "the research, drafting, PDF and organize agents";

const PROVIDER_ORDER: ModelProvider[] = ["openai", "anthropic", "google_genai"];

/** Catalog models grouped by provider, provider order fixed, catalog order kept. */
export const groupByProvider = (
  models: AgentModel[]
): Array<{ provider: ModelProvider; models: AgentModel[] }> =>
  PROVIDER_ORDER.map((provider) => ({
    provider,
    models: models.filter((m) => m.provider === provider)
  })).filter((g) => g.models.length > 0);

/** The copy under the picker for the current choice. */
export const describePick = (
  pick: string,
  models: AgentModel[],
  defaultLabel: string
): string => {
  if (pick === SUBAGENT_INHERIT_ID) {
    return `Runs ${AGENTS} on the model of the chat that starts them; ${defaultLabel} when started from the app.`;
  }
  const model = models.find((m) => m.id === pick);
  if (model) {
    return `Runs ${AGENTS} on ${model.label}. ${model.description}`;
  }
  return `Runs ${AGENTS} on ${defaultLabel}.`;
};

const Divider = () => <div className="h-px w-full bg-fill-1" />;

export const MOVED_COPY = "Firm Instructions moved to Case Preparation.";

type Props = {
  /** The caller's firm; the firm-wide sections are hidden without one. */
  lawFirmId?: string | null;
  /** Owner / admin (or platform superadmin): may edit the firm-wide sections. */
  canEditFirmInstructions?: boolean;
  /** Open another settings section; the Firm Instructions pointer uses it. */
  onOpenSection?: (section: string) => void;
};

export const AgentsTab: FC<Props> = ({
  lawFirmId = null,
  canEditFirmInstructions = false,
  onOpenSection
}) => {
  const dispatch = useDispatch();
  const uid = useSelector(lawyerSelectors.selectUid);
  const savedModel = useSelector(lawyerSelectors.selectSubagentModel);
  const savedThinking = useSelector(lawyerSelectors.selectSubagentThinking);
  const { models, loading, error } = useAgentSpecs();
  const [saving, setSaving] = useState(false);

  const defaultModel = models.find((m) => m.id === DEFAULT_MODEL_ID) ?? null;
  const defaultLabel = defaultModel?.label ?? DEFAULT_MODEL_LABEL;
  // A stored id no longer in the catalog (retired, or its provider is not
  // configured here) reads as Default; the server treats it the same way.
  const pick = useMemo(() => {
    if (savedModel === SUBAGENT_INHERIT_ID) return SUBAGENT_INHERIT_ID;
    return models.some((m) => m.id === savedModel) ? savedModel : "";
  }, [models, savedModel]);
  const picked = models.find((m) => m.id === pick) ?? null;
  // Default runs at the agents' built-in effort and Inherit follows the chat's
  // own switch, so the switch is only live for a named model that can think.
  const canThink = Boolean(picked?.supports_thinking);
  const thinking = canThink && savedThinking;
  const groups = useMemo(() => groupByProvider(models), [models]);

  const save = async (model: string, nextThinking: boolean) => {
    const previous = { model: savedModel, thinking: savedThinking };
    dispatch(setSubagentModelPick({ model, thinking: nextThinking }));
    if (!uid) return;
    setSaving(true);
    try {
      await LawyerAPI.update(uid, {
        subagentModel: model,
        subagentThinking: nextThinking
      });
    } catch {
      dispatch(setSubagentModelPick(previous));
      toast.error("Couldn't save the subagent model. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const onModelChange = (value: string) => {
    const model = value === DEFAULT_OPTION ? "" : value;
    const next = models.find((m) => m.id === model) ?? null;
    // Thinking only carries over to a model that can use it.
    void save(model, Boolean(next?.supports_thinking) && savedThinking);
  };

  const selectValue = pick === "" ? DEFAULT_OPTION : pick;

  return (
    <div className="mt-6 w-full" data-testid="agents-tab">
      <Divider />
      <div className="flex w-full items-start justify-between gap-6 px-6 py-4">
        <div className="flex min-w-0 flex-col items-start gap-1">
          <span className="text-body font-body text-default-font">
            Subagent model
          </span>
          <span className="text-caption font-caption text-subtext-color">
            {describePick(pick, models, defaultLabel)}
          </span>
        </div>
        <Select
          value={selectValue}
          onValueChange={onModelChange}
          disabled={loading || saving || Boolean(error)}
        >
          <SelectTrigger
            aria-label="Subagent model"
            data-testid="subagent-model-select"
            data-model={pick || DEFAULT_OPTION}
            className="w-56 shrink-0"
          >
            <SelectValue placeholder={loading ? "Loading…" : DEFAULT_LABEL} />
          </SelectTrigger>
          <SelectContent position="popper" align="end">
            <SelectItem
              value={DEFAULT_OPTION}
              data-testid="subagent-option-default"
            >
              {DEFAULT_LABEL}
              <span className="text-muted-foreground">({defaultLabel})</span>
            </SelectItem>
            <SelectItem
              value={SUBAGENT_INHERIT_ID}
              data-testid="subagent-option-inherit"
            >
              {INHERIT_LABEL}
            </SelectItem>
            {groups.map((group) => (
              <div key={group.provider}>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>{PROVIDER_LABEL[group.provider]}</SelectLabel>
                  {group.models.map((m) => (
                    <SelectItem
                      key={m.id}
                      value={m.id}
                      data-testid={`subagent-option-${m.id}`}
                    >
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </div>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Divider />
      <div className="flex w-full items-center justify-between gap-6 px-6 py-4">
        <div className="flex min-w-0 flex-col items-start gap-1">
          <span className="text-body font-body text-default-font">
            Thinking
          </span>
          <span className="text-caption font-caption text-subtext-color">
            {pick === SUBAGENT_INHERIT_ID
              ? "Follows the chat's Thinking switch."
              : picked
              ? canThink
                ? `Extended reasoning on ${picked.label}; slower and costlier per run.`
                : `${picked.label} does not offer extended reasoning.`
              : `${defaultLabel} runs at its standard reasoning effort.`}
          </span>
        </div>
        <Switch
          aria-label="Thinking"
          data-testid="subagent-thinking"
          checked={thinking}
          disabled={!canThink || saving}
          onCheckedChange={(checked) => void save(pick, checked)}
        />
      </div>
      <Divider />
      {error ? (
        <p className="px-6 pt-3 text-caption font-caption text-subtext-color">
          Couldn't load the model list: {error}
        </p>
      ) : null}
      {lawFirmId ? (
        <>
          <div
            className="flex w-full items-center justify-between gap-6 px-6 py-3"
            data-testid="firm-instructions-moved"
          >
            <span className="text-sm text-fg-3">{MOVED_COPY}</span>
            {onOpenSection ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOpenSection(FIRM_INSTRUCTIONS_SECTION)}
                data-testid="firm-instructions-moved-link"
              >
                Open Firm Instructions
                <ArrowRight />
              </Button>
            ) : null}
          </div>
          <Divider />
          <FirmApprovalsSection
            lawFirmId={lawFirmId}
            canEdit={canEditFirmInstructions}
          />
          <Divider />
        </>
      ) : null}
    </div>
  );
};
